/**
 * Member detail Net Exposure — Lab `getPlayerBooksForBackend`.
 *
 * Strix has no separate player-books route; Lab's filter.user = viewed player
 * maps to `POST /v1/book/getBooksForBackend` with that member id (Total Book).
 */

import { useQuery } from '@tanstack/react-query'
import { cmApi } from '@/api/cmApi'
import { authHeaders, readAuth } from '@/auth/session'
import { backendGroupsToEvents } from '@/api/map/books'
import { toMemberPlayerBooksLayout } from '@/api/map/memberPlayerBooks'
import { keys } from '@/api/hooks/shared/keys'
import { useBookmakerSessionFlag } from '@/api/hooks/useCmMe'
import { ROLE_LABEL } from '@/features/hierarchy/hierarchy'
import type { BookmakerSessionFlag } from '@/lib/bookmakerSessionFlag'
import type { NetExposureEvent } from '@/features/net-exposure/fixture'

/**
 * Lab `getPlayerBooksForBackend` always scopes rows to the viewed player id.
 * Use Member so SQL is `player.id = $user`.
 */
const PLAYER_BOOKS_LEVEL = ROLE_LABEL.player

/**
 * CM-API `booksForBackendSchema` — no `skipNegation`, no `filter.status`
 * (those Lab fields 400 with additionalProperties: false).
 */
export function playerBooksRequest(
  playerId: string,
  bookmakerSessionFlag: BookmakerSessionFlag = 'all',
) {
  return {
    selectedType: 'Total Book' as const,
    filter: {
      user: playerId,
      level: PLAYER_BOOKS_LEVEL,
      eventType: 'All',
      eventname: 'All',
      bookmakerSessionFlag,
    },
  }
}

/** Exported for tests — failures propagate so `usePlayerBooks().isError` is live. */
export async function loadPlayerBooks(
  playerId: string,
  bookmakerSessionFlag: BookmakerSessionFlag = 'all',
): Promise<NetExposureEvent[]> {
  if (!playerId) return []
  const groups = await cmApi.getBooksForBackend(
    authHeaders(),
    playerBooksRequest(playerId, bookmakerSessionFlag),
  )
  return toMemberPlayerBooksLayout(backendGroupsToEvents(groups ?? [], 'All'))
}

/** Lab member-tab sportsbook books for the selected player. */
export function usePlayerBooks(playerId: string | undefined) {
  const auth = readAuth()
  const bookmakerSessionFlag = useBookmakerSessionFlag()
  return useQuery({
    queryKey: keys.playerBooks({
      playerId: playerId ?? '',
      level: PLAYER_BOOKS_LEVEL,
      bookmakerSessionFlag,
    }),
    queryFn: () => loadPlayerBooks(playerId!, bookmakerSessionFlag),
    enabled: Boolean(auth && playerId),
    staleTime: 30_000,
    placeholderData: (previous) => previous,
  })
}
